/**
 * CORE-1749 — что человек выделил в сообщении Агапеллы на вебе.
 *
 * Тело сообщения в ленте помечено `data-fragment-source="<id>"` (`dataSet` у
 * react-native-web). Выделение засчитывается, только если ОБА его конца внутри
 * одного такого тела: выделение через два сообщения или с захватом времени и
 * цитаты над текстом — это не фраза из сообщения, и кнопка не появляется.
 * Второй рубеж — та же проверка, что сделает сервер (`isFragmentOf`): если
 * DOM отдал что-то, чего в тексте нет, лучше не предлагать «Спросить об этом»,
 * чем получить отказ после отправки.
 *
 * Без DOM-типов и без React: объекты описаны тем, что из них читается, и
 * проверяются в node подделками.
 */
import { cleanReplyFragment, isFragmentOf } from '../../utils/replyFragment';

export const FRAGMENT_SOURCE_ATTR = 'data-fragment-source';

interface ElementLike {
  closest?(selector: string): ElementLike | null;
  getAttribute(name: string): string | null;
}

interface NodeLike {
  nodeType?: number;
  parentElement?: ElementLike | null;
}

export interface RectLike {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
}

export interface SelectionLike {
  isCollapsed: boolean;
  rangeCount: number;
  anchorNode: unknown;
  focusNode: unknown;
  toString(): string;
  getRangeAt(index: number): { getBoundingClientRect(): RectLike };
}

export interface FragmentSelection {
  messageId: string;
  text: string;
  rect: RectLike;
}

/** id сообщения, в теле которого лежит узел, или null. */
export function fragmentSourceOf(node: unknown): string | null {
  const n = node as NodeLike | null | undefined;
  if (!n) return null;
  const el = (n.nodeType === 1 ? (n as unknown as ElementLike) : n.parentElement) ?? null;
  const source = el?.closest?.(`[${FRAGMENT_SOURCE_ATTR}]`) ?? null;
  return source?.getAttribute(FRAGMENT_SOURCE_ATTR) ?? null;
}

/**
 * Выделенная фраза или null. `textOf` — исходный текст сообщения по id (null —
 * такого сообщения в ленте нет).
 */
export function readFragmentSelection(
  selection: SelectionLike | null | undefined,
  textOf: (messageId: string) => string | null | undefined,
): FragmentSelection | null {
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;
  const anchor = fragmentSourceOf(selection.anchorNode);
  if (!anchor || fragmentSourceOf(selection.focusNode) !== anchor) return null;
  const text = cleanReplyFragment(selection.toString());
  if (!text) return null;
  const original = textOf(anchor);
  if (original == null || !isFragmentOf(text, original)) return null;
  const r = selection.getRangeAt(0).getBoundingClientRect();
  return {
    messageId: anchor,
    text,
    rect: { top: r.top, left: r.left, bottom: r.bottom, right: r.right, width: r.width, height: r.height },
  };
}

/**
 * Где поставить кнопку: под выделением по центру, а если снизу не помещается —
 * над ним; по горизонтали — в пределах окна с полями 8 px.
 */
export function askButtonPosition(
  rect: RectLike,
  viewport: { width: number; height: number },
  button: { width: number; height: number },
  gap = 8,
): { top: number; left: number } {
  const margin = 8;
  const below = rect.bottom + gap;
  const top = below + button.height <= viewport.height - margin
    ? below
    : Math.max(margin, rect.top - gap - button.height);
  const centre = rect.left + rect.width / 2 - button.width / 2;
  const left = Math.min(Math.max(margin, centre), Math.max(margin, viewport.width - button.width - margin));
  return { top, left };
}
